import { Injectable } from '@angular/core';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { DRResponse } from '@app/shared/models/response';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
    BaseHttpService,
    Create,
    GetById, Patch,
    Query,
    Update,
    UriBuilderParams
} from '@app/core/services/base/base-http.service';
import {
    DistributionDocument,
    DistributionJob,
    DistributionJobChannels,
    DistributionJobItem
} from '@app/shared/models/distribution-doc-models';
import { BaseWithId, HasId } from '@app/shared/models/base';
import { Case } from '@app/shared/models/case';
import { DrQueryParams, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { BaseWithIdList} from '@app/shared/models/list';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { TranslationEntry } from '@app/shared/models/translatable';
import { Receiver } from '@app/shared/models/participant';
import { Workflow, CaseWorkflowSections, WorkflowTargets, CaseWorkflowSettingsTypes } from '@app/shared/models/workflow';
import { DrFileMetaCategoryList } from '@app/shared/models/dr-file';


export abstract class DistributionJobUriBuilderParams extends UriBuilderParams<DistributionJob> {
    private readonly _item: string;

    get item() {
        return this._item;
    }

    protected constructor(item?: HasId | string, job?: DistributionJob | string) {
        super(job);
        this._item = this.getParamId(item);
    }

    public abstract buildBaseUri(): string;
    public abstract buildJobUri(): string;
    public abstract buildUri(): string;

}

export class CaseDistributionJobUriBuilderParams extends DistributionJobUriBuilderParams {

    constructor(item?: DistributionJobItem | Case | string, job?: DistributionJob | string) {
        super(item, job);
    }

    public buildBaseUri(): string {
        return `/${BackendUriUnits.DISTRIBUTION}/${BackendUriUnits.JOBS}`;
    }

    public buildJobUri(): string {
        let uri = this.buildBaseUri();

        if (this.base) {
            uri += `/${this.base}`;
        }

        return uri;
    }

    public buildUri(): string {
        let uri = this.buildJobUri();

        if (this.item) {
            uri += `/items/${this.item}`;
        }

        return uri;
    }
}


export class WorkflowDistributionJobUriBuilderParams extends DistributionJobUriBuilderParams {

    constructor(workflow: Workflow | string, job?: DistributionJob | string) {
        super(workflow, job);
    }

    public buildBaseUri(): string {
        return this.buildJobUri();
    }

    public buildUri(): string {
        return this.buildJobUri();
    }

    public buildJobUri(): string {
        let uri = `/${BackendUriUnits.WORKFLOW}/${BackendUriUnits.WORKFLOWS}/${WorkflowTargets.SMP_CASE}/${this.item}/${CaseWorkflowSections.DEFAULTS}/${CaseWorkflowSettingsTypes.DISTRIBUTIONS}`;

        if (this.base) {
            uri += `/${this.base}`;
        }

        return uri;
    }
}


@Injectable({
    providedIn: 'root'
})
export class DistributionService extends BaseHttpService<DistributionJob, DistributionJobUriBuilderParams>
                                 implements Query<DistributionJob, DistributionJobUriBuilderParams>,
                                            GetById<DistributionJob, DistributionJobUriBuilderParams>,
                                            Create<DistributionJob, DistributionJobUriBuilderParams>,
                                            Update<DistributionJob, DistributionJobUriBuilderParams>,
                                            Patch<DistributionJob, DistributionJobUriBuilderParams> {

    constructor(httpServiceBundle: HttpServiceBundle) {
        super(DistributionJob, httpServiceBundle, true);
    }

    // BASE METHODS

    public query(uriParams: DistributionJobUriBuilderParams, params: DrQueryParams): Observable<BaseWithIdList<DistributionJob>> {
        const uri = `${this.uri}${uriParams.buildBaseUri()}`;
        return this.http.get<DRResponse<any>>(uri, { params: params.queryParams })
                        .pipe(map(result => {
                            const list = new BaseWithIdList<DistributionJob>(DistributionJob);
                            list.items = result.data.map(job => DistributionJob.createSubTypeByChannel(job));
                            return list;
                        }));
    }

    public getById(uriParams: DistributionJobUriBuilderParams): Observable<DistributionJob> {
        const uri = this.constructUri(uriParams);
        return this.http.get<DRResponse<DistributionJob>>(uri)
                        .pipe(map(result => DistributionJob.createSubTypeByChannel(result.data)));
    }

    public create(uriParams: DistributionJobUriBuilderParams, job: DistributionJob): Observable<DistributionJob> {
        const uri = this.constructUri(uriParams);
        return this.http.post<DRResponse<DistributionJob>>(uri, job)
                        .pipe(map(result => DistributionJob.createSubTypeByChannel(result.data)));
    }

    public update(uriParams: DistributionJobUriBuilderParams, job: DistributionJob): Observable<DistributionJob> {
        const uri = this.constructUri(uriParams);
        return this.http.put<DRResponse<DistributionJob>>(uri, job)
                        .pipe(map(result => DistributionJob.createSubTypeByChannel(result.data)));
    }

    public patch(uriParams: DistributionJobUriBuilderParams, data: OpData[]): Observable<DistributionJob> {
        return this.patchBase(uriParams, data);
    }

    public patchAndUpdate(uriParams: DistributionJobUriBuilderParams, jobToUpdate: DistributionJob, data: OpData[]): Observable<DistributionJob> {
        return this.patchAndUpdateBase(uriParams, jobToUpdate, data, undefined, true);
    }

    public delete(uriParams: DistributionJobUriBuilderParams): Observable<boolean> {
        return this.deleteBase(uriParams);
    }


    // JOB PROCESSING

    public prepareDistributionJob(uriParams: DistributionJobUriBuilderParams): Observable<DistributionJob> {
        const uri = `${this.uri}${uriParams.buildJobUri()}/schedule_preparation`;
        return this.http.post<DRResponse<DistributionJob>>(uri, {})
                        .pipe(map(res => DistributionJob.createSubTypeByChannel(res.data)));
    }

    public sendDistributionJob(uriParams: DistributionJobUriBuilderParams): Observable<DistributionJob> {
        const uri = `${this.uri}${uriParams.buildJobUri()}/schedule_sending`;
        return this.http.post<DRResponse<DistributionJob>>(uri, {})
                        .pipe(map(res => DistributionJob.createSubTypeByChannel(res.data)));
    }

    public copyDistributionJob(uriParams: DistributionJobUriBuilderParams): Observable<DistributionJob> {
        const uri = `${this.uri}${uriParams.buildJobUri()}/copy`;
        return this.http.post<DRResponse<DistributionJob>>(uri, {})
                        .pipe(map(res => DistributionJob.createSubTypeByChannel(res.data)));
    }


    // DOCUMENTS (FILES)

    public getDistributionJobItemAssignables(uriParams: DistributionJobUriBuilderParams): Observable<DrFileMetaCategoryList> {
        const uri = `${this.uri}${uriParams.buildUri()}/files`;
        return this.http.get<DRResponse<DrFileMetaCategoryList>>(uri)
                        .pipe(map(res => new DrFileMetaCategoryList(res.data)));
    }

    public getDistributionJobDocuments(uriParams: DistributionJobUriBuilderParams): Observable<BaseWithIdList<DistributionDocument>> {
        const uri = `${this.uri}${uriParams.buildUri()}/documents`;
        return this.http.get<DRResponse<DistributionDocument[]>>(uri)
                        .pipe(map(res => new BaseWithIdList(DistributionDocument, res.data)));
    }

    public addDocumentToDistributionJob(uriParams: DistributionJobUriBuilderParams, doc: BaseWithId): Observable<DistributionDocument> {
        const uri = `${this.uri}${uriParams.buildUri()}/documents`;
        return this.http.post<DRResponse<DistributionDocument>>(uri, doc)
                        .pipe(map(res => new DistributionDocument(res.data)));
    }

    public updateDistributionJobDocument(uriParams: DistributionJobUriBuilderParams, doc: BaseWithId): Observable<DistributionDocument> {
        const uri = `${this.uri}${uriParams.buildUri()}/documents/${doc.id}`;
        return this.http.put<DRResponse<DistributionDocument>>(uri, doc)
                        .pipe(map(res => new DistributionDocument(res.data)));
    }

    public removeDocumentFromDistributionJob(uriParams: DistributionJobUriBuilderParams, doc: BaseWithId): Observable<boolean> {
        const uri = `${this.uri}${uriParams.buildUri()}/documents/${doc.id}`;
        return this.http.delete<DRResponse<boolean>>(uri)
                        .pipe(map(res => res.data));
    }


    // RECEIVERS

    public getDistributionJobItemReceivers(uriParams: DistributionJobUriBuilderParams): Observable<BaseWithIdList<Receiver>> {
        const uri = `${this.uri}${uriParams.buildJobUri()}/receivers`;
        return this.http.get<DRResponse<BaseWithIdList<Receiver>>>(uri)
                        .pipe(map(result => new BaseWithIdList<Receiver>(Receiver, result.data)));
    }


    // CHANNELS

    public getDistributionJobChannels(uriParams: DistributionJobUriBuilderParams): Observable<TranslationEntry[]> {
        const uri = `${this.uri}${uriParams.buildJobUri()}/channels`;
        return this.http.get<DRResponse<string[]>>(uri)
                        .pipe(map(res => res.data.map(channel => DistributionJobChannels.getByKey(channel))));
    }
}
